import React from "react";
import { motion } from "framer-motion";
import { Music2 } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useIsAndroidApp } from "@/hooks/useLaunchContext";

/**
 * Homepage "brand story" showcase: an emotional lyric video telling Kalypso Music's own story
 * (people creating their own personalized songs), with the app logo as thumbnail/visual anchor.
 * Placed right under the Hero to give first-time visitors something engaging to look at
 * immediately, before the "How it works" explainer section.
 * Web-only, same pattern as Footer.jsx: hidden inside the Android TWA app.
 */
export default function BrandStorySong() {
  const { language } = useI18n();
  const isEn = language === "en";
  const isAndroidApp = useIsAndroidApp();
  if (isAndroidApp) return null;

  return (
    <section className="px-4 py-16 sm:px-6 lg:px-8" data-testid="brand-story-song-section">
      <div className="mx-auto max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="overflow-hidden rounded-3xl border border-primary/20 bg-card shadow-[0_0_60px_-15px_rgba(168,85,247,0.35)]"
        >
          <div className="flex items-center gap-2 border-b border-border/60 px-5 py-4">
            <Music2 className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-foreground/90">
              {isEn ? "Our story, told in a song" : "Notre histoire, racontée en chanson"}
            </span>
          </div>
          <video
            data-testid="brand-story-song-video"
            className="aspect-video w-full bg-black"
            src="/media/kalypso-music-histoire.mp4"
            poster="/media/kalypso-music-histoire-poster.jpg"
            controls
            preload="metadata"
            playsInline
          />
          <div className="px-5 py-4 text-center">
            <p className="text-sm text-muted-foreground">
              {isEn
                ? "A song generated by Kalypso Music, about Kalypso Music - written, composed and sung entirely by AI."
                : "Une chanson générée par Kalypso Music, à propos de Kalypso Music - écrite, composée et chantée entièrement par IA."}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
